// src/components/DeliveryNoteForm.tsx (version finale avec select emplacement par produit)

import { useEffect, useMemo, useState } from 'react';
import { createResource,getResources,deleteResource, updateResource} from '../../../router/api';
import toast from 'react-hot-toast';

interface DeliveryNoteFormProps {
  order: any;          // BCC obligatoire
  onClose: () => void;
  onSuccess?: (bl: any) => void;
}

export default function DeliveryNoteForm({ order, onClose, onSuccess }: DeliveryNoteFormProps) {
  const [formData, setFormData] = useState({
    bon_commande_client_id: order.id,
    date_livraison: new Date().toISOString().split('T')[0],
    adresse_livraison: order.client.adresse_livraison || '',
    notes: '',
    lignes: order.lignes.map((l: any) => ({
      ligne_bcc_id: l.id, // 🔥 CLÉ MÉTIER
      produit_id: l.produit_id,
      designation: l.designation,
      quantite_commandee: l.quantite_commandee,
      quantite_livree: l.quantite_livree || 0,
      reliquat: l.quantite_commandee - (l.quantite_livree || 0),
      quantite_a_livrer: 0,
      emplacement_id: null as number | null,
    }))
  });

  const [emplacementsParProduit, setEmplacementsParProduit] = useState<Record<number, any[]>>({});
  const [loadingEmplacements, setLoadingEmplacements] = useState(false);

  const [loading, setLoading] = useState(false);

  // Chargement des emplacements pour chaque produit dès l'ouverture
  useEffect(() => {
    const loadEmplacements = async () => {
      setLoadingEmplacements(true);
      const map: Record<number, any[]> = {};
      await Promise.all(
        order.lignes.map(async (l: any) => {
          try {
            const res = await getResources(`bons-livraison/produits/${l.produit_id}/stock-par-emplacement`);
            map[l.produit_id] = res.data.data ?? [];
          } catch {
            map[l.produit_id] = [];
          }
        })
      );
      setEmplacementsParProduit(map);
      setLoadingEmplacements(false);
    };
    loadEmplacements();
  }, [order]);

  const handleQuantityChange = (index: number, value: number) => {
    const newLignes = [...formData.lignes];
    const max = newLignes[index].reliquat;
    newLignes[index].quantite_a_livrer = Math.max(0, Math.min(value, max));
    setFormData(prev => ({ ...prev, lignes: newLignes }));
  };

  const handleEmplacementChange = (index: number, emplacementId: number) => {
    const newLignes = [...formData.lignes];
    newLignes[index].emplacement_id = emplacementId;
    setFormData(prev => ({ ...prev, lignes: newLignes }));
  };

  const getTotalToDeliver = () => {
    return formData.lignes.reduce((sum, l) => sum + l.quantite_a_livrer, 0);
  };

  const getDeliveryStatus = () => {
    const totalCommande = formData.lignes.reduce((sum, l) => sum + l.quantite_commandee, 0);
    const totalDejaLivre = formData.lignes.reduce((sum, l) => sum + l.quantite_livree, 0);
    const totalCetteLivraison = getTotalToDeliver();

    if (totalDejaLivre + totalCetteLivraison >= totalCommande) {
      return 'Livraison Complète';
    }
    return totalCetteLivraison > 0 ? 'Livraison Partielle' : 'Aucune livraison';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (getTotalToDeliver() === 0) {
      toast.error('Veuillez saisir au moins une quantité à livrer');
      return;
    }

    // Vérification emplacement obligatoire
    const missingEmplacement = formData.lignes.some(l => l.quantite_a_livrer > 0 && !l.emplacement_id);
    if (missingEmplacement) {
      toast.error('Veuillez sélectionner un emplacement pour chaque ligne à livrer');
      return;
    }

    const payload = {
      bon_commande_client_id: formData.bon_commande_client_id,
      date_livraison: formData.date_livraison,
      adresse_livraison: formData.adresse_livraison || null,
      notes: formData.notes || null,
      lignes: formData.lignes
        .filter(l => l.quantite_a_livrer > 0)
        .map(l => ({
        ligne_bcc_id: l.ligne_bcc_id, // 🔥 OBLIGATOIRE
        quantite: l.quantite_a_livrer,
        emplacement_id: l.emplacement_id,
      }))
    };

    setLoading(true);
    try {
      const res = await createResource(`bons-livraison`, payload);
      toast.success(`Bon de Livraison créé : ${res.data.data.numero}`);
      onSuccess?.(res.data.data);
      onClose();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Erreur lors de la création du BL');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-6xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-medium text-gray-900">
              Création d'un Bon de Livraison — BCC {order.numero}
            </h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Informations générales */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Client</label>
                <input
                  type="text"
                  value={order.client.raison_sociale}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date de livraison</label>
                <input
                  type="date"
                  required
                  value={formData.date_livraison}
                  onChange={e => setFormData(prev => ({ ...prev, date_livraison: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Statut livraison</label>
                <div className={`px-4 py-2 rounded font-medium text-white ${getDeliveryStatus() === 'Livraison Complète' ? 'bg-green-600' : 'bg-orange-600'}`}>
                  {getDeliveryStatus()}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Adresse de livraison</label>
                <input
                  type="text"
                  value={formData.adresse_livraison}
                  onChange={e => setFormData(prev => ({ ...prev, adresse_livraison: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="Adresse complète"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes de livraison</label>
                <textarea
                  value={formData.notes}
                  onChange={e => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="Instructions spéciales..."
                />
              </div>
            </div>

            {/* Articles à livrer */}
            <div>
              <h4 className="text-lg font-medium text-gray-900 mb-4">Articles à livrer</h4>
              {loadingEmplacements && <p className="text-center text-gray-500">Chargement des emplacements...</p>}
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Produit</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Commandé</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Déjà livré</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Reliquat</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">À livrer</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Emplacement</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {formData.lignes.map((item, index) => {
                      const emplacements = emplacementsParProduit[item.produit_id] || [];
                      const hasStock = emplacements.some((e: any) => e.quantite > 0);

                      return (
                        <tr key={index}>
                          <td className="px-4 py-3 text-sm text-gray-900 font-medium">{item.designation}</td>
                          <td className="px-4 py-3 text-center text-sm">{item.quantite_commandee}</td>
                          <td className="px-4 py-3 text-center text-sm">{item.quantite_livree}</td>
                          <td className="px-4 py-3 text-center text-sm font-medium">{item.reliquat}</td>
                          <td className="px-4 py-3 text-center">
                            <input
                              type="number"
                              min="0"
                              max={item.reliquat}
                              value={item.quantite_a_livrer}
                              onChange={e => handleQuantityChange(index, parseInt(e.target.value) || 0)}
                              className="w-20 px-2 py-1 border border-gray-300 rounded text-center text-sm"
                              disabled={item.reliquat === 0}
                            />
                          </td>
                          <td className="px-4 py-3 text-center">
                            {item.quantite_a_livrer > 0 ? (
                              <select
                                required
                                value={item.emplacement_id || ''}
                                onChange={e => handleEmplacementChange(index, Number(e.target.value))}
                                className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                              >
                                <option value="">-- Choisir emplacement --</option>
                                {emplacements.map((emp: any) => (
                                  <option key={emp.emplacement_id} value={emp.emplacement_id}>
                                    {emp.emplacement_nom} ({emp.quantite} en stock)
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <span className="text-gray-400 text-sm">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Boutons */}
            <div className="flex justify-end space-x-3 pt-6 border-t">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={loading || getTotalToDeliver() === 0}
                className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Création...' : 'Créer le Bon de Livraison'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}